import { useState } from "react"
import { BREAKPOINTS, IMAGE_SIZES } from "@/constants/ui"
import { useHydrated } from "@/hooks/use-hydrated"

/** A larger rendition from a breakpoint up (see Photo `sources`). */
export interface PhotoSource {
  breakpoint: Exclude<keyof typeof BREAKPOINTS, "split">
  width: keyof typeof IMAGE_SIZES
}

/**
 * The <source>s for Photo's <picture>, widest first since the browser takes
 * the first match, and the rendition for its img's src.
 *
 * The src is `width`, the rendition below the smallest breakpoint, except for
 * a photo the browser renders rather than hydrates: WebKit fetches an img's
 * src before React puts the img in its <picture>, so a fallback different from
 * the source the browser then picks downloads for nothing. That photo takes the
 * source matching at mount instead. A hydrated photo keeps the server's src,
 * which Firefox would fetch again if it changed mid-download.
 */
export function usePhotoSources(
  width: keyof typeof IMAGE_SIZES,
  sources: PhotoSource[] | undefined
) {
  const pictureSources = sources
    ?.map((source) => ({
      media: `(min-width: ${BREAKPOINTS[source.breakpoint]}px)`,
      minWidth: BREAKPOINTS[source.breakpoint],
      width: source.width,
    }))
    .sort((a, b) => b.minWidth - a.minWidth)
  const hydrated = useHydrated()
  const [widthAtMount] = useState(() =>
    !hydrated
      ? undefined
      : pictureSources?.find(
          (source) => window.matchMedia(source.media).matches
        )?.width
  )
  return { pictureSources, srcWidth: widthAtMount ?? width }
}
